import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import useAuth from "src/hooks/useAuth";
import useLocales from "src/hooks/useLocales";
import { initiateSSOToApp } from "src/utils/sso";
import * as Yup from "yup";

const schema = Yup.object().shape({
  email: Yup.string()
    .email("Email must be a valid email address")
    .required("Email is required"),
  password: Yup.string().required("Password is required"),
});

const getRememberedEmail = () => {
  try {
    return localStorage.getItem("lastLoginEmail") || "";
  } catch {
    return "";
  }
};

const defaultValues = {
  email: getRememberedEmail(),
  password: "",
  remember: true,
};

// Map raw backend/network messages to translation keys so the user sees a
// human, localized error instead of "Wrong credentials!" or "Network Error".
const mapErrorMessage = (rawMessage, translate) => {
  const msg = String(rawMessage || "").toLowerCase().trim();
  if (!msg || msg === "network error" || msg.includes("network")) {
    return translate("auth.login.errors.networkError");
  }
  if (msg.includes("wrong credentials")) {
    return translate("auth.login.errors.wrongCredentials");
  }
  if (msg.includes("user is blocked")) {
    return translate("auth.login.errors.userBlocked");
  }
  if (msg.includes("admin cant login") || msg.includes("admin can't login")) {
    return translate("auth.login.errors.adminCantLogin");
  }
  if (msg.includes("2fa") || msg.includes("two-factor")) {
    return translate("auth.login.errors.twoFactorRequired");
  }
  // Unknown shape — fall back to the raw message if it's user-readable,
  // otherwise to a generic localized string.
  return /^[a-z0-9 .,'!?-]+$/i.test(rawMessage || "")
    ? rawMessage
    : translate("auth.login.errors.generic");
};

const useLogin = () => {
  const { login, twoFactorVerify, getUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const [searchParams] = useSearchParams();
  // Challenge del secondo passo: valorizzato solo se l'utente ha la verifica
  // in due passaggi attiva. Finche' e' presente, la form mostra il campo
  // codice al posto di email+password.
  const [twoFactor, setTwoFactor] = useState(null);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { reset, setError, handleSubmit, getValues } = methods;

  const onSubmit = async (data) => {
    console.log('[useLogin] onSubmit called');
    console.log('[useLogin] Current URL:', window.location.href);
    console.log('[useLogin] returnUrl from searchParams:', searchParams.get('returnUrl'));

    const {
      status,
      data: username,
      message,
      twoFactorRequired,
      challenge,
    } = await login(data.email, data.password, data.remember);

    console.log('[useLogin] Login response - status:', status, 'username:', username);

    // Password giusta ma serve il secondo fattore: la form passa al campo
    // codice, la sessione non e' ancora aperta.
    if (twoFactorRequired) {
      setTwoFactor({ challenge });
      return;
    }

    if (status) {
      await handleSuccess(username);
    } else {
      console.error('[useLogin] Login failed:', message);
      setError("afterSubmit", {
        message: mapErrorMessage(message, translate),
      });
    }
  };

  // Secondo passo: codice dell'app di autenticazione o codice di recupero.
  // A esito positivo prosegue esattamente come il login a un passo (stesso
  // handleSuccess: snackbar, SSO verso l'app se c'e' returnUrl, getUser).
  const onSubmitCode = async (data) => {
    const { status, data: username, message } = await twoFactorVerify(
      twoFactor.challenge,
      data.code,
      getValues("email"),
      getValues("remember")
    );

    if (status) {
      await handleSuccess(username);
      return;
    }
    // Challenge scaduto/consumato: si riparte da email+password.
    if (String(message || "").includes("challenge_expired")) {
      setTwoFactor(null);
      setError("afterSubmit", { message: translate("auth.login.twoFactorExpired") });
      return;
    }
    setError("afterSubmit", { message: translate("auth.login.twoFactorInvalid") });
  };

  async function handleSuccess(username) {
      // Check if there's a returnUrl (coming from summus_app)
      const returnUrl = searchParams.get('returnUrl');
      console.log('[useLogin] Login successful, username:', username);
      console.log('[useLogin] returnUrl from URL:', returnUrl);
      console.log('[useLogin] Current URL after login:', window.location.href);

      // Show success feedback immediately so the user gets visual confirmation
      // before any page transition.
      enqueueSnackbar(translate("auth.login.welcomeUser", { username }), {
        variant: "success",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });

      // Short hold so the snackbar is perceived and the page change doesn't
      // feel like an abrupt cut. Awaited inside handleSubmit → isSubmitting
      // stays true → LoadingButton keeps spinning during the pause.
      await new Promise((resolve) => setTimeout(resolve, 600));

      if (returnUrl) {
        const appUrl = process.env.REACT_APP_APP_URL || 'http://app.summus_network.localhost';
        const isSSOLink = returnUrl === appUrl || returnUrl.startsWith(appUrl);

        console.log('[useLogin] Processing returnUrl...');
        console.log('  - App URL:', appUrl);
        console.log('  - Is SSO link:', isSSOLink);

        if (isSSOLink) {
          // Use secure PKCE SSO flow for Summus App
          console.log('[useLogin] ✅ Using PKCE SSO flow (SECURE)');
          initiateSSOToApp(returnUrl);
          return;
        } else {
          // For other external URLs, use direct redirect (if needed in the future)
          console.log('[useLogin] ⚠️  External URL detected, redirecting directly');
          window.location.href = returnUrl;
          return;
        }
      } else {
        console.log('[useLogin] No returnUrl - following normal flow');
      }

      // Normal login flow (no returnUrl) — snackbar already shown above.
      console.log('[useLogin] Calling getUser() for normal flow');
      getUser();
      reset();
  }

  return {
    methods,
    onSubmit: handleSubmit(onSubmit),
    // Il secondo passo non passa dallo schema Yup di email+password: il campo
    // codice si valida da solo (obbligatorio) nel componente.
    onSubmitCode: handleSubmit(onSubmitCode),
    twoFactorRequired: Boolean(twoFactor),
    cancelTwoFactor: () => setTwoFactor(null),
  };
};

export default useLogin;
